import React, { useState, useEffect } from "react";
import { collection, getDocs, getFirestore } from "firebase/firestore";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import dayjs from "dayjs";
import { useUser } from "../UserContext";

const db = getFirestore();

export default function BookingDialog({
  open,
  onClose,
  selectedSlot,
  selectedDate,
  totalCourts,
  onBookingCreated = () => {},
  onProceedToPayment,
}) {
  const { memberData } = useUser();

  const [courtsToBook, setCourtsToBook] = useState(1);
  const [bookingDuration, setBookingDuration] = useState(30);

  const [searchQuery, setSearchQuery] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);

  useEffect(() => {
    if (open && selectedSlot) {
      setCourtsToBook(1);
      setBookingDuration(30);
      setSearchQuery("");
      setMemberResults([]);

      if (memberData) {
        setSelectedMembers([
          {
            id: memberData.squareCustomerId,
            firstName: memberData.firstName,
            lastName: memberData.lastName,
            email: memberData.email,
          },
        ]);
      } else {
        setSelectedMembers([]);
      }
    }
  }, [open, selectedSlot, memberData]);

  const handleSearchMembers = async () => {
    try {
      const q = searchQuery.toLowerCase();
      const memSnap = await getDocs(collection(db, "members"));
      const allMems = memSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const results = allMems.filter((m) => {
        const fname = (m.firstName || "").toLowerCase();
        const lname = (m.lastName || "").toLowerCase();
        const email = (m.email || "").toLowerCase();
        return fname.includes(q) || lname.includes(q) || email.includes(q);
      });

      setMemberResults(results);
    } catch (err) {
      console.error("Member search error:", err);
    }
  };

  const handleAddMember = (member) => {
    if (!selectedMembers.some((m) => m.id === member.id)) {
      setSelectedMembers((prev) => [...prev, member]);
    }
  };

  const handleRemoveMember = (memberId) => {
    if (selectedMembers.length === 1 && selectedMembers[0].id === memberId) {
      alert("You must have at least one member selected.");
      return;
    }
    setSelectedMembers((prev) => prev.filter((m) => m.id !== memberId));
  };

  const handleBookingSave = () => {
    if (!selectedSlot) return;
    const { free, startDayjs } = selectedSlot;
    const toBook = parseInt(courtsToBook, 10);

    if (selectedMembers.length === 0) {
      if (memberData) {
        setSelectedMembers([
          {
            id: memberData.id,
            firstName: memberData.firstName,
            lastName: memberData.lastName,
            email: memberData.email,
          },
        ]);
      } else {
        alert("Please add at least one member.");
        return;
      }
    }
    if (toBook < 1 || toBook > free) {
      alert(`Invalid number of courts. You can only book 1 to ${free}.`);
      return;
    }

    const endDayjs = startDayjs.add(bookingDuration, "minute");
    const endOfDay = selectedDate.startOf("day").hour(24).minute(0);
    if (endDayjs.isAfter(endOfDay)) {
      alert("Booking crosses into next day. Reduce duration.");
      return;
    }

    const tempBooking = {
      date: selectedDate.format("YYYY-MM-DD"),
      startTime: startDayjs.format("HH:mm"),
      endTime: endDayjs.format("HH:mm"),
      courtsUsed: toBook,
      members: selectedMembers,
    };

    onClose();
    if (onProceedToPayment) {
      onProceedToPayment(tempBooking);
    }
  };

  if (!selectedSlot) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Court Booking: {selectedSlot?.displayRange || ""}
      </DialogTitle>

      <DialogContent dividers>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Start: {selectedSlot.startDayjs.format("h:mm A")} on{" "}
            {selectedDate.format("DD/MM/YYYY")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Free Courts: {selectedSlot.free}
          </Typography>

          <TextField
            label="Number of Courts to Book"
            type="number"
            fullWidth
            margin="normal"
            value={courtsToBook}
            onChange={(e) => setCourtsToBook(e.target.value)}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="durationLabel">Duration</InputLabel>
            <Select
              labelId="durationLabel"
              value={bookingDuration}
              label="Duration"
              onChange={(e) =>
                setBookingDuration(parseInt(e.target.value, 10))
              }
            >
              <MenuItem value={30}>30 mins</MenuItem>
              <MenuItem value={60}>1 hour</MenuItem>
              <MenuItem value={90}>1 hr 30 mins</MenuItem>
              <MenuItem value={120}>2 hours</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Add Members</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <TextField
                label="Search name/email"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Button variant="contained" onClick={handleSearchMembers}>
                Search
              </Button>
            </Stack>

            {memberResults.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2">Search Results:</Typography>
                  {memberResults.map((m) => (
                    <Box
                      key={m.id}
                      sx={{ p: 1, mb: 1, bgcolor: "#f5f5f5", cursor: "pointer" }}
                      onClick={() => handleAddMember(m)}
                    >
                      <Typography variant="body2">
                        {m.firstName} {m.lastName} ({m.email})
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            {selectedMembers.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2">
                    Selected Members:
                  </Typography>
                  {selectedMembers.map((m) => (
                    <Box
                      key={m.id}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 1,
                        mb: 1,
                        bgcolor: "#e0f7fa",
                      }}
                    >
                      <Typography variant="body2">
                        {m.firstName} {m.lastName} ({m.email})
                      </Typography>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleRemoveMember(m.id)}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleBookingSave}>
          Confirm Booking
        </Button>
      </DialogActions>
    </Dialog>
  );
}
